/**
 * Body shape charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nails_shades`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const bodyShapeChartsPack: ToilePack = {
    slug: 'body-shape-charts',
    category: 'beauty',
    name: 'Body shape charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'shapes', label: 'Five shapes', subject: 'a chart of five body shape silhouettes, "Hourglass", "Pear", "Apple", "Rectangle" and "Inverted triangle", each labelled'},
        {key: 'measure', label: 'Measuring guide', subject: 'a chart showing where to measure "Bust", "Waist" and "Hips" on a silhouette'},
        {key: 'dressing', label: 'What to wear', subject: 'a chart pairing each body shape silhouette with a dress outline'},
    ],
    styles: [
        {key: 'line', label: 'Line diagram', style: 'technical line art with uniform thin black outlines and dashed guide lines', palette: 'black ink only', framing: 'the figures in an even row across the page with a wide white margin'},
        {key: 'flat', label: 'Flat color', style: 'clean line art with each silhouette filled in one smooth flat colour, no gradients', palette: 'each figure in one soft flat colour of blush, sage, sand, lilac or sky, outlines in charcoal, the page left white', framing: 'the figures in an even row across the page with a wide white margin'},
        {key: 'sketch', label: 'Croquis sketch', style: 'a fashion illustration croquis in a loose tapered ink line with light marker shading down one side of each figure', palette: 'black ink with pale gray marker', framing: 'the figures tall on the page with a white margin all round'},
    ],
    directions: ["men's shapes", 'swimwear guide', 'plus size'],
    directionHint: "try: men's shapes",
    seo: {
        title: 'Body Shape Charts: Five Shapes, Measuring, Dressing',
        metaDescription: 'Body shape charts: five silhouettes, a measuring guide for bust, waist and hips, and a dress chart, in three drawing styles.',
        h1: 'Body shape charts: five shapes, measuring, what to wear',
        tagline: 'Nine drawing pages: the five shapes, a measuring guide and a dressing chart, each in three styles.',
        intro: 'A body shape chart for dressing to your shape. The five silhouettes are hourglass, pear, apple, rectangle and inverted triangle. A measuring guide marks where to take the bust, waist and hip measures, and a dressing chart pairs each shape with a dress outline. Each page comes as a line diagram, in flat colour or as a croquis sketch. Type a different group or a twist, such as swimwear guide or plus size, and the same drawing style carries it.',
        useCases: [
            'a fitting-room reference for a client',
            'a styling lesson in a fashion class',
            'a handout for a sewing or dressmaking group',
            'a shape guide for planning a wardrobe',
        ],
        faq: [
            {q: 'What are the five shapes?', a: 'Hourglass, pear, apple, rectangle and inverted triangle.'},
            {q: 'Where does the measuring guide put the tape?', a: 'At the bust, the waist and the hips, marked on a standing figure.'},
            {q: 'What does the dressing chart show?', a: 'A dress outline paired with each of the five body shapes.'},
            {q: 'Which drawing style should I pick?', a: 'The line diagram is plain black ink. The flat colour gives each figure one soft colour. The croquis sketch is a loose fashion drawing with light grey marker shading.'},
        ],
    },
}
